/**
 * Query key factory (05 §5.1). Every `useQuery`/`invalidateQueries` call in the
 * app goes through this object so a key is never spelled out twice.
 */
import type { RollStatusFilter } from '@hadir/shared';

export interface StudentListFilters {
  cohortId?: string;
  q?: string;
  status?: 'ACTIVE' | 'SUSPENDED';
}

export interface SectionListFilters {
  courseId?: string;
  from?: string;
  to?: string;
}

export interface DeviceListFilters {
  status?: 'ACTIVE' | 'PENDING_APPROVAL' | 'REVOKED';
}

/**
 * The filters of `GET /sections/:id/attendance` — the section roll.
 *
 * They belong in the key, not beside it: switching the status chip from ABSENT
 * to NOT_RECORDED must start a new cache entry, otherwise TanStack Query serves
 * the previous filter's rows until the refetch lands and the counts and the
 * table disagree on screen for a second. `RollStatusFilter` is the shared enum
 * (`PRESENT | LATE | ABSENT | EXCUSED | NOT_RECORDED`).
 */
export interface SectionRollFilters {
  status?: RollStatusFilter;
  q?: string;
}

/**
 * `GET /courses/:id/grades` varies on every one of these, so they belong in the
 * key: sorting by `scorePct` while a key built from the id alone is cached
 * would show the previous ordering until the refetch lands.
 */
export interface CourseGradesFilters {
  sort?: string;
  dir?: 'asc' | 'desc';
  atRisk?: boolean;
}

/** The matrix takes the same filters plus its column cap. */
export interface CourseMatrixFilters extends CourseGradesFilters {
  maxSections?: number;
}

export const keys = {
  health: ['health'] as const,
  healthReady: ['health', 'ready'] as const,
  healthVersion: ['health', 'version'] as const,

  me: ['me'] as const,

  cohorts: ['cohorts'] as const,
  cohort: (id: string) => ['cohorts', id] as const,

  students: (filters: StudentListFilters) => ['students', filters] as const,
  student: (id: string) => ['students', id] as const,

  courses: ['courses'] as const,
  course: (id: string) => ['courses', id] as const,
  courseReport: (id: string) => ['courses', id, 'report'] as const,
  courseGradingPolicy: (id: string) => ['courses', id, 'grading-policy'] as const,
  courseAtRisk: (id: string) => ['courses', id, 'at-risk'] as const,
  /**
   * The grades list and the matrix. `courseMatrix` deliberately nests UNDER the
   * existing `courseReport` prefix, so `invalidateQueries({ queryKey:
   * keys.courseReport(id) })` — what saving the grading policy already does —
   * still reaches every filtered matrix without that call having to change.
   */
  courseGrades: (id: string, filters: CourseGradesFilters = {}) =>
    ['courses', id, 'grades', filters] as const,
  courseMatrix: (id: string, filters: CourseMatrixFilters = {}) =>
    ['courses', id, 'report', filters] as const,

  sections: (filters: SectionListFilters) => ['sections', filters] as const,
  section: (id: string) => ['sections', id] as const,
  sectionAttendance: (id: string) => ['sections', id, 'attendance'] as const,
  /**
   * The roll, filters included — the key `sectionAttendance` should have been.
   * It shares the `['sections', id, 'attendance']` prefix on purpose, so one
   * `invalidateQueries({ queryKey: keys.sectionAttendance(id) })` still covers
   * every filter combination a screen has cached.
   */
  sectionRoll: (id: string, filters: SectionRollFilters = {}) =>
    ['sections', id, 'attendance', filters] as const,
  sectionReport: (id: string) => ['sections', id, 'report'] as const,
  sectionAttempts: (id: string) => ['sections', id, 'attempts'] as const,

  devices: (filters: DeviceListFilters) => ['devices', filters] as const,

  halls: ['halls'] as const,
  settings: ['settings'] as const,
  users: ['users'] as const,
  auditLogs: ['audit-logs'] as const,
} as const;
